import { atom } from 'jotai';
import {
  CancelUnsubscribeParams,
  PendingUnsubscribe,
  ScheduleUnsubscribeParams,
} from '../types';
import { createSubscriptionKey, setDebugLabels } from '../utils/atomUtils';
import {
  clearAllTimeouts,
  clearExistingTimeout,
  scheduleUnsubscribeTimeout,
  UNSUBSCRIBE_DELAY_MS,
} from '../utils/debounceUtils';
import {
  addPendingUnsubscribe,
  executeDebouncedUnsubscribe,
  executeForceUnsubscribe,
  flushAllPendingUnsubscribes,
  logCancelledUnsubscribe,
  logFlushingUnsubscribes,
  logScheduledUnsubscribe,
  removePendingUnsubscribe,
} from '../utils/pendingUnsubscribeUtils';

const pendingTimeoutsAtom = atom<Map<string, NodeJS.Timeout>>(new Map());

const pendingUnsubscribesAtom = atom<Map<string, PendingUnsubscribe>>(
  new Map(),
);

pendingTimeoutsAtom.onMount = (setAtom) => {
  return () => {
    let timeouts: Map<string, NodeJS.Timeout> = new Map();
    setAtom((prev) => {
      timeouts = prev;
      return new Map();
    });
    clearAllTimeouts(timeouts);
  };
};

export const scheduleDebouncedUnsubscribeAtom = atom(
  null,
  async (get, set, params: ScheduleUnsubscribeParams) => {
    const { topic, subscriberId, force = false } = params;
    const key = createSubscriptionKey(topic, subscriberId);

    const currentTimeouts = get(pendingTimeoutsAtom);
    const clearedTimeouts = clearExistingTimeout(key, currentTimeouts);
    if (clearedTimeouts !== currentTimeouts) {
      set(pendingTimeoutsAtom, clearedTimeouts);
    }

    if (force) {
      const currentPending = get(pendingUnsubscribesAtom);
      if (currentPending.has(key)) {
        const newPending = removePendingUnsubscribe(key, currentPending);
        set(pendingUnsubscribesAtom, newPending);
      }

      await executeForceUnsubscribe(set, topic, subscriberId);
      return;
    }

    const currentPending = get(pendingUnsubscribesAtom);
    const newPending = addPendingUnsubscribe(
      key,
      topic,
      subscriberId,
      currentPending,
    );
    set(pendingUnsubscribesAtom, newPending);

    logScheduledUnsubscribe(topic, subscriberId, UNSUBSCRIBE_DELAY_MS);

    const timeoutId = scheduleUnsubscribeTimeout(async () => {
      await executeDebouncedUnsubscribe(
        get,
        set,
        key,
        topic,
        subscriberId,
        pendingUnsubscribesAtom,
        pendingTimeoutsAtom,
      );
    }, UNSUBSCRIBE_DELAY_MS);

    const timeouts = get(pendingTimeoutsAtom);
    const updatedTimeouts = new Map(timeouts);
    updatedTimeouts.set(key, timeoutId);
    set(pendingTimeoutsAtom, updatedTimeouts);
  },
);

export const cancelDebouncedUnsubscribeAtom = atom(
  null,
  (get, set, params: CancelUnsubscribeParams): boolean => {
    const { topic, subscriberId } = params;
    const key = createSubscriptionKey(topic, subscriberId);
    const currentPending = get(pendingUnsubscribesAtom);

    if (!currentPending.has(key)) {
      return false;
    }

    const currentTimeouts = get(pendingTimeoutsAtom);
    const clearedTimeouts = clearExistingTimeout(key, currentTimeouts);
    if (clearedTimeouts !== currentTimeouts) {
      set(pendingTimeoutsAtom, clearedTimeouts);
    }

    const newPending = removePendingUnsubscribe(key, currentPending);
    set(pendingUnsubscribesAtom, newPending);

    logCancelledUnsubscribe(topic, subscriberId);

    return true;
  },
);

export const flushPendingUnsubscribesAtom = atom(null, async (get, set) => {
  const pendingUnsubscribes = get(pendingUnsubscribesAtom);

  if (pendingUnsubscribes.size === 0) {
    return;
  }

  logFlushingUnsubscribes(pendingUnsubscribes.size);

  const currentTimeouts = get(pendingTimeoutsAtom);
  clearAllTimeouts(currentTimeouts);
  set(pendingTimeoutsAtom, new Map());

  await flushAllPendingUnsubscribes(set, pendingUnsubscribes);

  set(pendingUnsubscribesAtom, new Map());
});

export interface BatchUnsubscribeParams {
  topics: string[];
  subscriberId: string;
}

export const scheduleBatchDebouncedUnsubscribeAtom = atom(
  null,
  async (get, set, params: BatchUnsubscribeParams) => {
    const { topics, subscriberId } = params;

    if (topics.length === 0) {
      return;
    }

    // Clear existing timeouts for all topics in the batch
    let currentTimeouts = get(pendingTimeoutsAtom);
    let timeoutsModified = false;

    for (const topic of topics) {
      const key = createSubscriptionKey(topic, subscriberId);
      const clearedTimeouts = clearExistingTimeout(key, currentTimeouts);
      if (clearedTimeouts !== currentTimeouts) {
        currentTimeouts = clearedTimeouts;
        timeoutsModified = true;
      }
    }

    if (timeoutsModified) {
      set(pendingTimeoutsAtom, currentTimeouts);
    }

    // Add all topics to pending unsubscribes in one batch
    let currentPending = get(pendingUnsubscribesAtom);
    const newPending = new Map(currentPending);
    const timestamp = Date.now();

    for (const topic of topics) {
      const key = createSubscriptionKey(topic, subscriberId);
      newPending.set(key, { topic, subscriberId, timestamp });
    }

    set(pendingUnsubscribesAtom, newPending);

    // Schedule a single timeout for all topics
    const timeoutId = scheduleUnsubscribeTimeout(async () => {
      const pendingAtTimeout = get(pendingUnsubscribesAtom);

      // Process all topics in the batch
      for (const topic of topics) {
        const key = createSubscriptionKey(topic, subscriberId);
        if (pendingAtTimeout.has(key)) {
          await executeDebouncedUnsubscribe(
            get,
            set,
            key,
            topic,
            subscriberId,
            pendingUnsubscribesAtom,
            pendingTimeoutsAtom,
          );
        }
      }
    }, UNSUBSCRIBE_DELAY_MS);

    // Store the timeout reference for all topics
    const updatedTimeouts = new Map(get(pendingTimeoutsAtom));
    for (const topic of topics) {
      const key = createSubscriptionKey(topic, subscriberId);
      updatedTimeouts.set(key, timeoutId);
    }
    set(pendingTimeoutsAtom, updatedTimeouts);
  },
);

setDebugLabels({
  scheduleDebouncedUnsubscribeAtom,
  cancelDebouncedUnsubscribeAtom,
  flushPendingUnsubscribesAtom,
  scheduleBatchDebouncedUnsubscribeAtom,
  pendingUnsubscribesAtom,
  pendingTimeoutsAtom,
});
